/* Promo-Price Indicator: one listener, two modes.
     coded (FALL26)        - recompute the coded amount, keep the Apply link on
                             the selected variant.
     sale  (evergreen)     - restate price, "Save $X.XX", and the struck
                             compare-at from the selected variant; hide the
                             block when that variant has no real compare-at.
                             Spec: exports/evergreen-sale-callout/SPEC.md.
   The mode is read from data-coded-promo on each block.
   Keeps the PDP / quickview promo amount, Apply-code link, and visibility in
   sync with the selected variant. Companion to snippets/coded-price-indicator
   .liquid; loaded once globally via a deferred <script> in layout/theme.liquid
   (an asset, not an inline script, because inline scripts do not run inside the
   quickview HTML the theme injects with innerHTML, and would be absent on grid
   pages that render only the card context; a global load covers every surface).

   The theme dispatches a bubbling `theme:variant:change` from the product form /
   variant picker with the variant in event.detail. We scope updates to the
   enclosing product surface - the PDP section ([data-section-id]) or the quickview
   drawer ([data-quickview-modal] / .quickview__modal). Each holds both the form
   and the price block and is separate for the main PDP and a quickview, so the
   two never cross-update. (Note: the quickview renders the price OUTSIDE
   [data-product-form-wrapper], so that tighter wrapper cannot be used as the
   scope.) Fails safe: on any gap it leaves the server-rendered value.

   Behaviour per event:
     - valid variant  -> recompute coded = price - round(price*percent/100) (per
       unit, integer cents), reformat with the shop money format from
       data-coded-money-format, and rebuild the Apply-code redirect so it carries
       the selected variant.
     - no matching variant (event carries a null variant; the theme blanks the
       normal price and disables add-to-cart) -> hide the coded surface so a stale
       offer is never shown; it reappears when a valid variant is chosen again.

   Lifecycle: this file is PERMANENT infrastructure since the evergreen sale
   mode (PR #174) and the bundle disclosure sync landed in it. Do NOT delete it
   at the FALL26 teardown; only the coded campaign config expires (Oct 2
   2026, by its date window). The planned generic rename (coded-price.js ->
   promo-price.js, evergreen SPEC.md PR-3) carries all of this file's
   behavior forward, including the bundle disclosure sync. */
(function () {
  if (window.tcCodedPriceInit) return;
  window.tcCodedPriceInit = true;

  function fmt(cents, format) {
    format = format || '${{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function delim(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }
    var match = format.match(re);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals': value = delim(cents, 0); break;
      case 'amount_with_comma_separator': value = delim(cents, 2, '.', ','); break;
      case 'amount_with_space_separator': value = delim(cents, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = delim(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_comma_separator': value = delim(cents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator': value = delim(cents, 0, ' '); break;
      default: value = delim(cents, 2);
    }
    return format.replace(re, value);
  }

  // Apply-code: AJAX so the code is stored on the session and the button flips to
  // a clear "Code applied" state with no jarring navigation. Falls back to the
  // native discount link (which carries the selected variant) if the fetch fails,
  // and works with no JS at all because the element is a plain <a href>.
  function markApplied(a, container, code) {
    a.classList.remove('is-applying');
    a.classList.add('is-applied');
    a.setAttribute('aria-disabled', 'true');
    a.textContent = a.getAttribute('data-coded-applied-label') || 'Code applied';
    var status = container ? container.querySelector('[data-coded-status]') : null;
    if (status) status.textContent = 'Code ' + code + ' applied.';
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('[data-coded-apply]') : null;
    if (!a) return;
    if (a.classList.contains('is-applied') || a.classList.contains('is-applying')) {
      e.preventDefault();
      return;
    }
    var container = a.closest('[data-coded-price]');
    var code = container && container.getAttribute('data-coded-code');
    if (!code) return; // no code available: let the native link navigate
    e.preventDefault();
    a.classList.add('is-applying');
    a.textContent = 'Applying';
    fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (res && res.ok) { markApplied(a, container, code); }
        else { window.location.href = a.href; } // HTTP error (expired/5xx): fall back to the native discount link
      })
      .catch(function () { window.location.href = a.href; });
  });

  document.addEventListener('theme:variant:change', function (e) {
    var scope = (e.target && e.target.closest)
      ? (e.target.closest('[data-quickview-modal], .quickview__modal, [data-section-id]') || document)
      : document;
    var v = e.detail && e.detail.variant;

    // Bundle price-anchor disclosure (snippets/bundle-price-disclosure.liquid):
    // visible only while the selected variant carries a real compare-at, in
    // either promo mode or none. Runs before the promo-node early-return so it
    // still tracks variants when no promo block is on the page.
    var bpd = scope.querySelector('[data-bundle-price-disclosure]');
    if (bpd) {
      bpd.hidden = !(v && typeof v.compare_at_price === 'number' && v.compare_at_price > v.price);
    }

    var nodes = scope.querySelectorAll('[data-coded-price][data-coded-mode="pdp"]');
    if (!nodes.length) return;
    // In-stock only: hide the offer when there is no matching variant OR the
    // selected variant is sold out, so we never advertise a coded price for
    // something the shopper cannot buy.
    var showOffer = v && typeof v.price === 'number' && v.available;

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var promo = el.getAttribute('data-coded-promo');
      // Bundle mode is sale-mode machinery with bundle labels; the label text
      // itself rides data-coded-save-label, so one branch serves both.
      var isSale = promo === 'sale' || promo === 'bundle';
      var format = el.getAttribute('data-coded-money-format');

      // Sale mode needs a REAL compare-at on the selected variant as well.
      // compare_at_price rides the same variant object the theme dispatches
      // (theme.js reads variant.compare_at_price off it in getPrices /
      // setProductState), and is null when merchandising has not set one.
      var saleOk = showOffer;
      if (isSale) {
        saleOk = showOffer &&
          typeof v.compare_at_price === 'number' &&
          v.compare_at_price > v.price;
      }

      if (!saleOk) {
        el.hidden = true;
        continue;
      }
      el.hidden = false;

      if (isSale) {
        var sAmt = el.querySelector('[data-coded-amount]');
        if (sAmt) sAmt.textContent = fmt(v.price, format);
        var sSave = el.querySelector('[data-coded-save]');
        // Label comes from the theme's translation for products.product.save,
        // rendered onto the element, so a variant change does not swap a
        // localized label back to English.
        var saveLabel = el.getAttribute('data-coded-save-label') || 'Save';
        if (sSave) sSave.textContent = saveLabel + ' ' + fmt(v.compare_at_price - v.price, format);
        var sWas = el.querySelector('[data-coded-was]');
        if (sWas) sWas.textContent = fmt(v.compare_at_price, format);
        continue;
      }

      var amt = el.querySelector('[data-coded-amount]');
      if (amt) {
        var pct = parseInt(el.getAttribute('data-coded-percent'), 10) || 20;
        var coded = v.price - Math.round((v.price * pct) / 100);
        // textContent is safe (no markup): the shop money format is the plain
        // "${{amount}}" (verified), matching the Liquid | money output.
        amt.textContent = fmt(coded, el.getAttribute('data-coded-money-format'));
      }

      // Keep the Apply-code redirect on the variant the shopper is looking at.
      var apply = el.querySelector('[data-coded-apply]');
      var code = el.getAttribute('data-coded-code');
      var url = el.getAttribute('data-coded-url');
      if (apply && code && url) {
        apply.href = '/discount/' + code + '?redirect=' + encodeURIComponent(url + '?variant=' + v.id);
      }
    }
  });
})();
